import { ComponentProps } from "react";

import EnvironmentConfiguration from "../../../Constants/EnvironmentConfiguration";
import SessionContextValueProvider from "../../../Providers/Value/SessionContextValueProvider";
import ComposableMedicalForm from "../Components/ComposableMedicalForm";
import DisablerMedicalFormItemDecorator from "../Components/FormItemsAndDecorators/DisablerMedicalFormItemDecorator";
import NoElementFormItem from "../Components/FormItemsAndDecorators/NoElementFormItem";
import SideEffectMedicalFormItemDecorator, {
  ISideEffectMedicalFormItemDecoratorConfig
} from "../Components/FormItemsAndDecorators/SideEffectMedicalFormItemDecorator";
import YesCheckboxFormItem from "../Components/FormItemsAndDecorators/YesCheckboxFormItem";
import YesNoToggleFormItem from "../Components/FormItemsAndDecorators/YesNoToggleFormItem";
import ComposableMedicalFormInterpreter from "../Types/ComposableMedicalFormInterpreter";
import IMedicalFormInterpreter from "../Types/IMedicalFormInterpreter";
import { IMedicalFormItemProps } from "../Types/IMedicalFormItemProps";

/** The ids of question responses that should be ignored by the form interpreter. */
const ignorableFormItemIds = {
  cancer: "medicalFormItem_cancerTreatment",
  diabetic: "medicalFormItem_diabetic",
  pacemaker: "medicalFormItem_pacemaker"
};

const dialysisQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesCheckboxFormItem({
    ...props,
    formItemId: "medicalFormItem_dialysis",
    messages: [{ text: "Are you on Dialysis?" }],
    ChildFormItem: null
  });

const oxygenTankQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesCheckboxFormItem({
    ...props,
    formItemId: "medicalFormItem_oxygenTank",
    messages: [{ text: "Do you use an Oxygen tank while awake?" }],
    ChildFormItem: null
  });

const organTransplantQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesCheckboxFormItem({
    ...props,
    formItemId: "medicalFormItem_organTransplant",
    messages: [{ text: "Have you had an organ transplant or are you currently on the organ transplant waiting list?" }],
    ChildFormItem: null
  });

const bleedingDisorderQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesCheckboxFormItem({
    ...props,
    formItemId: "medicalFormItem_bleedingDisorder",
    messages: [{ text: "Do you have a bleeding disorder?" }],
    ChildFormItem: null
  });

const recentBloodClotQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesCheckboxFormItem({
    ...props,
    formItemId: "medicalFormItem_recentBloodClot",
    messages: [{ text: "Have you had a blood clot in the last 12 months?" }],
    ChildFormItem: null
  });

const noDefibrillatorFormItemId = "medicalFormItem_pacemakerDefibrillator";

const noDefibrillatorDialogueFormItem: React.FC<IMedicalFormItemProps> = props =>
  NoElementFormItem({
    ...props,
    formItemId: "medicalFormItem_noDefibrillatorDialogue",
    messages: [
      {
        text: "If No: Thank you for sharing. During your consultation, we will review your medical history in greater detail as this could impact your eligibility for surgery."
      }
    ],
    ChildFormItem: null
  });

const disablesDefibrillatorDialogueDecorator: React.FC<IMedicalFormItemProps> = props =>
  DisablerMedicalFormItemDecorator({
    ...props,
    isDisabled: form => form[noDefibrillatorFormItemId] !== "false",
    FormItem: noDefibrillatorDialogueFormItem
  });

const defibrillatorFormItemId = "medicalFormItem_pacemakerDefibrillator";
const defibrillatorQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesNoToggleFormItem({
    ...props,
    formItemId: defibrillatorFormItemId,
    messages: [{ text: "If Yes: Does it have a defibrillator?" }],
    ChildFormItem: disablesDefibrillatorDialogueDecorator
  });

const disablesDefibrillatorDecorator: React.FC<IMedicalFormItemProps> = props =>
  DisablerMedicalFormItemDecorator({
    ...props,
    isDisabled: form => form[ignorableFormItemIds.pacemaker] !== "true",
    FormItem: defibrillatorQuestion
  });

const storesPacemakerDataDefibrillatorDecorator: React.FC<
  IMedicalFormItemProps & Pick<ISideEffectMedicalFormItemDecoratorConfig, "setMedicalConclusions">
> = props =>
  SideEffectMedicalFormItemDecorator({
    ...props,
    transformConclusions: (oldState, { formId, value }) =>
      formId !== defibrillatorFormItemId
        ? null
        : {
            ...oldState,
            hasNonDefibrillatorPacemaker: value === "false" ? true : value === "true" ? false : null
          },
    transformFormState: null,
    FormItem: disablesDefibrillatorDecorator
  });

const composedDefibrillatorQuestion: React.FC<IMedicalFormItemProps> = SessionContextValueProvider<
  Pick<ISideEffectMedicalFormItemDecoratorConfig, "setMedicalConclusions">,
  ComponentProps<typeof storesPacemakerDataDefibrillatorDecorator>
>({
  Component: storesPacemakerDataDefibrillatorDecorator,
  map: ({ setMedicalConclusions }) => ({ setMedicalConclusions })
});

const pacemakerQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesCheckboxFormItem({
    ...props,
    formItemId: ignorableFormItemIds.pacemaker,
    messages: [{ text: "Do you have a pacemaker?" }],
    ChildFormItem: composedDefibrillatorQuestion
  });

const revertsDefibrillatorOnUnsetPacemakerDecorator: React.FC<
  IMedicalFormItemProps & Pick<ISideEffectMedicalFormItemDecoratorConfig, "setMedicalConclusions">
> = props =>
  SideEffectMedicalFormItemDecorator({
    ...props,
    transformConclusions: (oldState, { formId, value }) =>
      formId == ignorableFormItemIds.pacemaker && value !== "false"
        ? { ...oldState, hasNonDefibrillatorPacemaker: null }
        : null,
    transformFormState: (oldState, { formId, value }) =>
      formId == ignorableFormItemIds.pacemaker && value !== "false"
        ? { ...oldState, [defibrillatorFormItemId]: null }
        : null,
    FormItem: pacemakerQuestion
  });

const composedPacemakerQuestion: React.FC<IMedicalFormItemProps> = SessionContextValueProvider<
  Pick<ISideEffectMedicalFormItemDecoratorConfig, "setMedicalConclusions">,
  ComponentProps<typeof revertsDefibrillatorOnUnsetPacemakerDecorator>
>({
  Component: revertsDefibrillatorOnUnsetPacemakerDecorator,
  map: ({ setMedicalConclusions }) => ({ setMedicalConclusions })
});

const isChemoTreatingCancerFormItemId = "medicalFormItem_cancerChemo";

const cancerChemoQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesCheckboxFormItem({
    ...props,
    formItemId: isChemoTreatingCancerFormItemId,
    messages: [{ text: "If No: Are you undergoing chemotherapy treatments?" }],
    ChildFormItem: null
  });

const activeCancerQuestionId = "medicalFormItem_activeCancer";

const cancerChemoDisabledQuestion: React.FC<IMedicalFormItemProps> = props =>
  DisablerMedicalFormItemDecorator({
    ...props,
    isDisabled: form => form[activeCancerQuestionId] !== "false",
    FormItem: cancerChemoQuestion
  });

const activeCancerQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesNoToggleFormItem({
    ...props,
    formItemId: activeCancerQuestionId,
    messages: [{ text: "Do you have active cancer?" }],
    ChildFormItem: cancerChemoDisabledQuestion
  });

const disabledActiveCancerDecorator: React.FC<IMedicalFormItemProps> = props =>
  DisablerMedicalFormItemDecorator({
    ...props,
    isDisabled: form => form[ignorableFormItemIds.cancer] !== "true",
    FormItem: activeCancerQuestion
  });

const cancerDialogueQuestion: React.FC<IMedicalFormItemProps> = props =>
  NoElementFormItem({
    ...props,
    formItemId: "medicalFormItem_cancerChemoNoCancerDialogue",
    messages: [
      {
        text: "If Yes: Thank you for sharing. We are unable to work on any areas of the body previously treated with radiation."
      }
    ],
    ChildFormItem: disabledActiveCancerDecorator
  });

const disablesCancerDialogueDecorator: React.FC<IMedicalFormItemProps> = props =>
  DisablerMedicalFormItemDecorator({
    ...props,
    isDisabled: form => form[ignorableFormItemIds.cancer] !== "true",
    FormItem: cancerDialogueQuestion
  });

const cancerTreatmentQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesCheckboxFormItem({
    ...props,
    formItemId: ignorableFormItemIds.cancer,
    messages: [{ text: "Have you been treated for cancer?" }],
    ChildFormItem: disablesCancerDialogueDecorator
  });

const unsetCancerTreatmentDecorator: React.FC<
  IMedicalFormItemProps & Pick<ISideEffectMedicalFormItemDecoratorConfig, "setMedicalConclusions">
> = props =>
  SideEffectMedicalFormItemDecorator({
    ...props,
    transformConclusions: (oldState, { formId, value }) =>
      formId == ignorableFormItemIds.cancer ? { ...oldState, hasRadiationAreaRestrictions: value === "true" } : null,
    transformFormState: (oldState, { formId, value }) => {
      if (formId == ignorableFormItemIds.cancer && value !== "true")
        return { ...oldState, [isChemoTreatingCancerFormItemId]: null, [activeCancerQuestionId]: null };
      else if (formId == activeCancerQuestionId && value !== null)
        return { ...oldState, [isChemoTreatingCancerFormItemId]: null };
      return null;
    },
    FormItem: cancerTreatmentQuestion
  });

const composedCancerTreatmentQuestion: React.FC<IMedicalFormItemProps> = SessionContextValueProvider<
  Pick<ISideEffectMedicalFormItemDecoratorConfig, "setMedicalConclusions">,
  ComponentProps<typeof unsetCancerTreatmentDecorator>
>({
  Component: unsetCancerTreatmentDecorator,
  map: ({ setMedicalConclusions }) => ({ setMedicalConclusions })
});

const a1cDiabeticPassDialogueFormItem: React.FC<IMedicalFormItemProps> = props =>
  NoElementFormItem({
    ...props,
    formItemId: "medicalFormItem_a1cDiabeticPassDialogue",
    messages: [
      {
        text: `If Yes: Thank you. An A1C of ${EnvironmentConfiguration.a1cUpperLimit} or below is required for surgery. Please bring your most recent A1C reading to review during your consult.`
      }
    ],
    ChildFormItem: null
  });

const disablesA1cOnParentFalseDecorator: React.FC<IMedicalFormItemProps> = props =>
  DisablerMedicalFormItemDecorator({
    ...props,
    isDisabled: form => form[ignorableFormItemIds.diabetic] !== "true",
    FormItem: a1cDiabeticPassDialogueFormItem
  });

const diabeticQuestion: React.FC<IMedicalFormItemProps> = props =>
  YesCheckboxFormItem({
    ...props,
    formItemId: ignorableFormItemIds.diabetic,
    messages: [{ text: "Do you have diabetes?" }],
    ChildFormItem: disablesA1cOnParentFalseDecorator
  });

const storesDiabeticConclusionDecorator: React.FC<
  IMedicalFormItemProps & Pick<ISideEffectMedicalFormItemDecoratorConfig, "setMedicalConclusions">
> = props =>
  SideEffectMedicalFormItemDecorator({
    ...props,
    transformConclusions: (oldState, { value }) => ({
      ...oldState,
      diabetic: value !== "true" ? false : true
    }),
    transformFormState: null,
    FormItem: diabeticQuestion
  });

const composedDiabeticQuestion: React.FC<IMedicalFormItemProps> = SessionContextValueProvider<
  Pick<ISideEffectMedicalFormItemDecoratorConfig, "setMedicalConclusions">,
  ComponentProps<typeof storesDiabeticConclusionDecorator>
>({
  Component: storesDiabeticConclusionDecorator,
  map: ({ setMedicalConclusions }) => ({ setMedicalConclusions })
});

/** A configuration for interpreting the questions in the medical form, ignoring those with required follow-ups and
 * appropriate answer predicates for non-boolean questions.
 * @remarks This interpreter config is for the composed form as of 10/2025.
 */
export const MedicalFormInterpreter: IMedicalFormInterpreter = new ComposableMedicalFormInterpreter({
  keysToIgnore: Object.values(ignorableFormItemIds),
  answerPredicates: {}
});

/** The questions to be rendered in the medical form.
 * @remarks This is the composed list as of 10/2025.
 */
export const FormItems: ComponentProps<typeof ComposableMedicalForm>["formItems"] = [
  dialysisQuestion,
  oxygenTankQuestion,
  organTransplantQuestion,
  bleedingDisorderQuestion,
  recentBloodClotQuestion,
  composedPacemakerQuestion,
  composedCancerTreatmentQuestion,
  composedDiabeticQuestion
];
